import Dexie from 'dexie'
import { uuid } from '@addictivegroup/addictive-offline'
import { reactive } from 'vue'
import { api, upload, ApiError, NetworkError } from '@/lib/http.js'

/**
 * Photos, films and files added to a note on a phone, kept on the device until they reach Core
 * (D-143). A salesperson in a basement takes a photo, saves the note, and both arrive when the
 * phone is online again: the note through the offline queue, its files from here — each
 * uploaded, then attached to the note once Core has it. A store of the panel's own, beside
 * addictive-offline's database.
 *
 * Row: {id, noteUuid, name, mime, size, blob, createdAt, error}
 */
const db = new Dexie('addcrm-uploads')
db.version(1).stores({ uploads: 'id, noteUuid, createdAt' })

/** Rows per note, for the timeline ("2 files waiting"). */
export const pendingUploads = reactive({ byNote: {}, sending: false })

async function refresh() {
  const rows = await db.table('uploads').toArray()
  const byNote = {}
  for (const row of rows) (byNote[row.noteUuid] ||= []).push({ id: row.id, name: row.name, mime: row.mime, size: row.size, error: row.error || null })
  pendingUploads.byNote = byNote
}

/** Keeps files for a note and sends them as soon as it can. */
export async function queueNoteFiles(noteUuid, files) {
  for (const file of files) {
    await db.table('uploads').put({ id: uuid(), noteUuid, name: file.name, mime: file.type || 'application/octet-stream', size: file.size, blob: file, createdAt: Date.now(), error: null })
  }
  await refresh()
  return flushNoteFiles()
}

export async function forgetUpload(id) {
  await db.table('uploads').delete(id)
  await refresh()
}

let running = null

/** Sends what waits; stops at the first sign of no network. Resolves to the number sent. */
export function flushNoteFiles() {
  // The browser knows it is offline: nothing to try until it is back.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return Promise.resolve(0)
  running ??= (async () => {
    pendingUploads.sending = true
    let sent = 0
    try {
      const rows = await db.table('uploads').orderBy('createdAt').toArray()
      for (const row of rows) {
        try {
          const file = new File([row.blob], row.name, { type: row.mime })
          const temp = (await upload('/uploads/temp', file)).data
          await api(`/notes/${row.noteUuid}/attachments`, { method: 'POST', body: { upload_uuid: temp.uuid } })
          await db.table('uploads').delete(row.id)
          sent++
        } catch (e) {
          if (e instanceof NetworkError) break
          // The note is still in the offline queue: try again once it has arrived.
          if (e instanceof ApiError && e.status === 404) continue
          await db.table('uploads').update(row.id, { error: e?.message || String(e) })
        }
      }
    } finally {
      await refresh()
      pendingUploads.sending = false
      running = null
    }
    return sent
  })()
  return running
}

let started = false

/** Once per app: retry when the network comes back, after queued writes are delivered, and every minute. */
export function startNoteUploads(offline) {
  if (started) return
  started = true
  refresh()
  flushNoteFiles()
  window.addEventListener('online', () => flushNoteFiles())
  offline?.on?.((event) => {
    if (event === 'sent' || event === 'synced') flushNoteFiles()
  })
  setInterval(() => {
    if (Object.keys(pendingUploads.byNote).length) flushNoteFiles()
  }, 60000)
}
